import { useEffect, useState } from "react";

/**
 * Reusable auto-play primitive for the fixture-driven YaaS program-builder
 * doc blocks (Configuration, Policies, Viewing, Activation, and the Neobank
 * walkthroughs). Funding is no longer a standalone block — it's a derived
 * read-only Configuration field.
 *
 * A block holds its own draft fixture in local state and describes the
 * scripted sequence as an ordered list of {@link SimScriptStep}s — each a
 * `run()` that mutates that state (populate a field, flip a toggle, advance
 * a phase) plus an `after` delay (ms to wait *before* running it). The hook
 * fires each step on a timer, then loops after `loopPauseMs`.
 *
 * The block stays a plain props-only View; the driver is the only stateful
 * piece, and every block reuses this same hook — so the other four doc
 * blocks copy the Configuration shape verbatim and just swap their step
 * list. Nothing here touches the DOM, wallet, RPC, or `orgApiFetch`.
 *
 * Reduced-motion: when the user prefers reduced motion, the whole sequence
 * is applied in one synchronous pass (final state, no per-step timers, no
 * loop) so the block still shows its populated end-state without animating.
 */
export interface SimScriptStep {
    /** ms to wait before running this step (measured from the previous step). */
    after: number;
    /** Mutate the block's fixture state for this beat. */
    run: () => void;
    /** Optional label — handy when debugging a sequence. */
    label?: string;
}

/** Default per-digit cadence (ms) for {@link typedNumberBeats} — quick but
 *  legible, so a 3–6 digit value reads as "someone typing it" without dragging
 *  out the sequence. Mirrors the retail form's TYPE_CHAR_MS feel but a touch
 *  slower (these are single fields the viewer is watching, not a wallet
 *  address flying by). */
const TYPE_CHAR_MS_DEFAULT = 90;

export interface TypedNumberBeatsOptions {
    /** The final numeric value the field lands on (e.g. `90`, `250000`). */
    target: number;
    /**
     * Sets the field's value for a beat. Called with each progressive prefix
     * of `target` (9 → 90 → …) and finally with `target` itself. The sim's own
     * setter (patch) closes over which draft field this writes.
     */
    setValue: (value: number) => void;
    /** Per-digit cadence in ms. Defaults to {@link TYPE_CHAR_MS_DEFAULT}. */
    charMs?: number;
    /** ms to wait before the FIRST digit lands (after the field is focused).
     *  Defaults to `charMs`. */
    startAfter?: number;
    /** Optional label prefix for the generated beats (debugging). */
    label?: string;
}

/**
 * Expand a target numeric value into per-digit {@link SimScriptStep}s so a
 * scripted field TYPES its value in one digit at a time — the way a real user
 * would — instead of snapping to the final number in one beat. Mirrors the
 * per-char-typing precedent in `YaasPositionForm` / `YaasEnableRewardsSim`
 * (the retail Enable Rewards form types its label + rewards destination
 * character by character).
 *
 * Each successive prefix of the integer part of `target` is set on its own
 * beat: `90` yields `[9, 90]`; `250000` yields `[2, 25, 250, 2500, 25000,
 * 250000]`; a decimal like `7.1` types the integer digits then the fraction
 * (`7`, then `7.1`). The caller travels + focuses the field first (via the
 * control's move/hover/press beats); THIS helper only produces the value-build
 * beats that follow, so it slots in right after the focus press beat.
 *
 * The digit cadence is steady (`charMs`), the first digit landing `startAfter`
 * ms after focus. Reduced-motion is handled by {@link useSimScript} itself: it
 * runs every step synchronously in one pass, so the field simply ends on the
 * full `target` (the intermediate prefixes are applied and immediately
 * overwritten — no animation, correct final value).
 *
 * Pure — no DOM, cursor, or state of its own; just a beat list the sim splices
 * into its `steps`.
 */
export function typedNumberBeats({
    target,
    setValue,
    charMs = TYPE_CHAR_MS_DEFAULT,
    startAfter,
    label
}: TypedNumberBeatsOptions): SimScriptStep[] {
    // Build the progressive-prefix values from the target's decimal string.
    // Integer digits first (each new digit extends the number), then — for a
    // decimal target — the fraction lands as a final beat. Negative values
    // aren't used by these number fields, so `target` is treated as ≥ 0.
    const text = String(target);
    const prefixes: number[] = [];
    const [intPart, fracPart] = text.split(".");
    for (let i = 1; i <= intPart.length; i++) {
        prefixes.push(Number(intPart.slice(0, i)));
    }
    // A decimal value: type the whole thing on the last beat (the fraction
    // arrives as one step so we don't emit an invalid trailing-dot number).
    if (fracPart !== undefined) {
        prefixes.push(target);
    }
    // Dedupe consecutive equal values (e.g. a leading run) so we never emit a
    // no-op beat that stalls the cadence.
    const values = prefixes.filter((v, i) => i === 0 || v !== prefixes[i - 1]);

    return values.map((value, i) => ({
        after: i === 0 ? (startAfter ?? charMs) : charMs,
        label: label ? `${label}:type-${value}` : undefined,
        run: () => setValue(value)
    }));
}

export interface EditNumberFieldBeatsOptions<Control> {
    /** The control id the sim's `demo` prop routes forced hover/press to. */
    control: Control;
    /** The field's `data-sim-cursor-key` the cursor travels to. */
    cursorKey: string;
    /** Final numeric value the field is EDITED to (typed in after clearing). */
    target: number;
    /** ms the cursor spends travelling to the field before hover. */
    travelMs: number;
    /** ms the field holds its forced HOVER visual before the click. */
    hoverHoldMs: number;
    /** ms after focus before the FIRST typed digit lands. Defaults to 260. */
    typeStartAfter?: number;
    /** Travel the sim cursor to `cursorKey`. */
    moveCursor: (cursorKey: string, opts: { durationMs: number }) => void;
    /** Pulse the sim cursor's click visual. */
    clickCursor: () => void;
    /** Paint the control's forced hover/press visual (or clear it). */
    setDemo: (control: Control, phase: "hover" | "press") => void;
    /** Clear the field to its blank (cleared mid-edit) state — the pre-filled
     *  default is wiped so the field reads empty before the new value types. */
    clearValue: () => void;
    /** Set the field's value for a typed-prefix beat. */
    setValue: (value: number) => void;
    /** Optional label prefix for the generated beats (debugging). */
    label?: string;
}

/**
 * Beats for a numeric FIELD the cursor EDITS a PRE-FILLED value on: the field
 * OPENS already reading its current value (the real default), and the cursor
 * travels → hovers → clicks-to-focus → CLEARS it (blank) → TYPES the new value
 * digit-by-digit via {@link typedNumberBeats}. Distinct from a type-from-empty
 * field: the viewer sees the OPENING value, then it clears and the new value
 * builds up (e.g. `100 → blank → 8 → 80`, `14 → blank → 7`, `100000 → blank →
 * … → 250000`).
 *
 * Shared by every program-builder sim (Configuration + both Neobank
 * walkthroughs) so the "seed the real default, then demo the edit" treatment is
 * one helper. Generic over the sim's `Control` union (each sim has its own set
 * of demoable controls). The caller supplies its own cursor/demo/patch closures
 * and timing constants — this helper only assembles the beat list.
 *
 * Pure — no DOM, cursor, or state of its own; just a beat list the sim splices
 * into its `steps`.
 */
export function editNumberFieldBeats<Control>({
    control,
    cursorKey,
    target,
    travelMs,
    hoverHoldMs,
    typeStartAfter = 260,
    moveCursor,
    clickCursor,
    setDemo,
    clearValue,
    setValue,
    label = `cfg:${String(control)}`
}: EditNumberFieldBeatsOptions<Control>): SimScriptStep[] {
    return [
        {
            after: travelMs,
            label: `${label}:move`,
            run: () => moveCursor(cursorKey, { durationMs: travelMs })
        },
        {
            after: travelMs,
            label: `${label}:hover`,
            run: () => setDemo(control, "hover")
        },
        {
            after: hoverHoldMs,
            label: `${label}:focus`,
            run: (): void => {
                clickCursor();
                setDemo(control, "press");
                // Clear the pre-filled default to BLANK (null) so the field
                // reads empty before the new value types in.
                clearValue();
            }
        },
        ...typedNumberBeats({
            target,
            startAfter: typeStartAfter,
            label,
            setValue
        })
    ];
}

export interface UseSimScriptOptions {
    /**
     * Called once at the top of every cycle to reset the block's fixture to
     * its starting (empty/partial) state before the steps replay it.
     */
    reset: () => void;
    /** Ordered scripted beats. */
    steps: readonly SimScriptStep[];
    /**
     * Pause (ms) after the last step settles before the sequence replays.
     * Defaults to 4500. Set to `-1` to play once and stop.
     */
    loopPauseMs?: number;
}

const DEFAULT_LOOP_PAUSE_MS = 4500;

/**
 * Global playback slowdown for the scripted program-builder sims. Each step's
 * `after` delay is stretched by this factor so every beat — and therefore the
 * dwell on each page/state — lasts ~this-many times longer, giving viewers time
 * to read each screen. One lever keeps every block's relative pacing intact;
 * adjust it here rather than re-tuning every per-block timing constant.
 */
const PLAYBACK_SLOWDOWN = 2;

function prefersReducedMotion(): boolean {
    return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
}

/**
 * Drives {@link SimScriptStep}s on timers and loops. Returns the current
 * cycle index (bumps each loop) purely so a consumer can key off replays if
 * it ever needs to; most blocks ignore it.
 */
export function useSimScript({ reset, steps, loopPauseMs = DEFAULT_LOOP_PAUSE_MS }: UseSimScriptOptions): number {
    // Bumping `cycle` re-runs the effect, replaying from the top.
    const [cycle, setCycle] = useState(0);

    useEffect(() => {
        reset();

        // Reduced motion: apply every step immediately (final populated
        // state), no timers, no loop — a still, complete frame.
        if (prefersReducedMotion()) {
            steps.forEach((s) => s.run());
            return;
        }

        const timers: number[] = [];
        let at = 0;
        steps.forEach((step) => {
            at += step.after * PLAYBACK_SLOWDOWN;
            timers.push(window.setTimeout(step.run, at));
        });

        if (loopPauseMs >= 0) {
            timers.push(window.setTimeout(() => setCycle((c) => c + 1), at + loopPauseMs));
        }

        return (): void => timers.forEach((t) => window.clearTimeout(t));
        // `reset` / `steps` are recreated per render by the block; the block
        // memoizes them (or accepts the identity churn — the effect is
        // idempotent). `cycle` drives the loop; `loopPauseMs` the cadence.
    }, [cycle, loopPauseMs, reset, steps]);

    return cycle;
}
